import React from "react";
import { Table, Input, Select, Button, Checkbox } from "semantic-ui-react";
import { TrajectoryChart } from "../Paso4Objetivos/TrajectoryChart";
import "./Paso7Seguimiento.scss";

const fmt = (v, d = 1) => (isFinite(v) ? Number(v).toLocaleString("es-AR", { minimumFractionDigits: d, maximumFractionDigits: d }) : "—");
const fFecha = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("es-AR") : "—");
const num = (v) => { const n = parseFloat(String(v ?? "").replace(",", ".")); return isFinite(n) ? n : 0; };
const daysUntil = (iso) => (iso ? Math.ceil((new Date(iso + "T00:00:00") - new Date()) / 86400000) : null);

const CHANNEL_OPTIONS = [
  { key: "annual_report", value: "annual_report", text: "Informe anual" },
  { key: "sustainability_report", value: "sustainability_report", text: "Memoria de sostenibilidad" },
  { key: "website", value: "website", text: "Sitio web" },
  { key: "cdp_questionnaire", value: "cdp_questionnaire", text: "Cuestionario CDP" },
  { key: "other", value: "other", text: "Otro" },
];

// Mismos 5 disparadores del prototipo (línea 1048). Se guardan en el
// mismo mapa `criteria` que los 23 criterios del Paso 5 — es la misma
// reutilización que hacía el prototipo (S.crit sirve para cualquier id,
// no solo los c_*), no una decisión nueva de este port.
const TRIGGERS = [
  ["t_40", "El Alcance 3 pasa a ser ≥ 40% del agregado A1+2+3"],
  ["t_str", "Cambios estructurales significativos: adquisiciones, desinversiones, fusiones, in/outsourcing, cambios de portafolio"],
  ["t_err", "Ajustes significativos del inventario del año base o errores acumulados colectivamente significativos"],
  ["t_sup", "Cambios relevantes en proyecciones o supuestos usados al fijar el objetivo"],
  ["t_sme", "Cambia la elegibilidad para la ruta PYME (dispara recálculo según los indicadores PYME)"],
];

const COLABORACIONES = [
  "UNFCCC Fashion Industry Charter (net-zero 2050; −30% A1+2+3 a 2030)",
  "Sustainable Apparel Coalition · Higg Index (FEM/MSI)",
  "NRDC Clean by Design (teñido y acabado)",
  "Race to the Top (Vietnam)",
  "IFC — eficiencia de recursos (685.000 tCO₂e/año evitadas)",
  "IFC PaCT (caso Levi's: −20% GEI en 1 año en 6 sitios; > USD 1M ahorrados)",
  "We Mean Business",
  "Circular Fibres Initiative",
  "Fashion Positive",
  "Collaboration for Forests & Agriculture (cuero/deforestación)",
  "Gold Standard (intervenciones Scope 3)",
  "Making Zero Impact Fashionable (WWF/HSBC)",
  "CDP Supply Chain",
];

// Interpolación lineal base→objetivo para UNA sola fila — es la misma
// cuenta de presentación que ya hace TrajectoryChart para dibujar la
// línea del gráfico; el número que SÍ decide algo (el semáforo) viene
// siempre de computed.semaphore, calculado por el motor en el servidor.
function expectedAt(baseYear, targetYear, baseEmissions, targetEmissions, year) {
  if (!(baseYear && targetYear && baseEmissions > 0) || year < baseYear) return null;
  const f = Math.min(1, (year - baseYear) / (targetYear - baseYear || 1));
  return baseEmissions - (baseEmissions - targetEmissions) * f;
}

export function Paso7Seguimiento({ record, computed, patchSection }) {
  const tracking = record.tracking || [];
  const disclosures = record.disclosures || [];
  const criteria = record.criteria || {};
  const params = computed?.params || {};
  const semaphore = computed?.semaphore;
  const r12 = computed?.targetScope12 || {};
  const r3 = computed?.targetScope3 || {};
  const baseYear = Number(record.inventory?.baseYear);
  const targetYear12 = Number(record.targets?.scope12?.targetYear);
  const targetYear3 = Number(record.targets?.scope3?.targetYear);

  const setTracking = (next, opts) => patchSection("tracking", next, opts);
  const setRow = (ix, patch) => {
    const next = [...tracking];
    next[ix] = { ...next[ix], ...patch };
    setTracking(next);
  };
  const addRow = () => setTracking([...tracking, { year: "", scope1: "", scope2: "", scope3Covered: "", note: "", source: "manual" }], { immediate: true });
  const removeRow = (ix) => {
    const next = [...tracking];
    next.splice(ix, 1);
    setTracking(next, { immediate: true });
  };

  const setDisclosures = (next, opts) => patchSection("disclosures", next, opts);
  const setDisclosure = (ix, patch) => {
    const next = [...disclosures];
    next[ix] = { ...next[ix], ...patch };
    setDisclosures(next);
  };
  const addDisclosure = () => setDisclosures([...disclosures, { reportedYear: "", channel: "annual_report", date: "", reference: "" }], { immediate: true });
  const removeDisclosure = (ix) => {
    const next = [...disclosures];
    next.splice(ix, 1);
    setDisclosures(next, { immediate: true });
  };

  const setTrigger = (id, checked) => patchSection("criteria", { ...criteria, [id]: { ...(criteria[id] || {}), status: checked ? "ok" : "" } }, { immediate: true });

  const sorted = [...tracking].sort((a, b) => Number(a.year) - Number(b.year));
  const series12 = sorted.map((x) => ({ year: Number(x.year), value: num(x.scope1) + num(x.scope2) }));
  const series3 = sorted.map((x) => ({ year: Number(x.year), value: num(x.scope3Covered) }));

  const quinquenal = (computed?.dueDates || []).find((d) => d.label === "Disparo de la revisión quinquenal obligatoria");
  const quinquenalResultados = (computed?.dueDates || []).find((d) => d.label === "Entrega de resultados de la revisión (formulario online)");
  const quinquenalRetargets = (computed?.dueDates || []).find((d) => d.label === "Re-presentación de objetivos ajustados (si hace falta)");
  const diasRestantes = quinquenal ? daysUntil(quinquenal.date) : null;

  return (
    <div className="sbti-paso7">
      <div className="sbti-pagehead">
        <div className="sbti-crumb">Paso 7 · Vida del objetivo</div>
        <h2>Seguimiento anual, <span className="sbti-pgram sbti-pgram--mid"><span>divulgación</span></span> y revisión quinquenal</h2>
        <p className="sbti-sub">
          La aprobación no es el final: cada año hay que divulgar públicamente el inventario y el progreso, y a los{" "}
          {params.revision_anios ?? 5} años el SBTi exige revisar los objetivos.
        </p>
      </div>

      <div className="sbti-card">
        <h3><span className="sbti-tag">DATOS</span> Registros anuales de emisiones</h3>
        <Table celled compact className="sbti-paso7__table">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Año</Table.HeaderCell>
              <Table.HeaderCell>A1 (tCO₂e)</Table.HeaderCell>
              <Table.HeaderCell>A2 (tCO₂e)</Table.HeaderCell>
              <Table.HeaderCell>A3 cubierto (tCO₂e)</Table.HeaderCell>
              <Table.HeaderCell>A1+2</Table.HeaderCell>
              <Table.HeaderCell>Trayectoria A1+2</Table.HeaderCell>
              <Table.HeaderCell>Origen</Table.HeaderCell>
              <Table.HeaderCell>Nota</Table.HeaderCell>
              <Table.HeaderCell></Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {tracking.map((r, ix) => {
              const expected = expectedAt(baseYear, targetYear12, r12.baseEmissions, r12.targetEmissions, Number(r.year));
              return (
                <Table.Row key={ix}>
                  <Table.Cell><Input type="number" value={r.year || ""} onChange={(_, d) => setRow(ix, { year: d.value })} style={{ width: 84 }} /></Table.Cell>
                  <Table.Cell>
                    <Input type="number" step="0.01" value={r.scope1 || ""} disabled={r.source === "gei"} onChange={(_, d) => setRow(ix, { scope1: d.value })} />
                  </Table.Cell>
                  <Table.Cell>
                    <Input type="number" step="0.01" value={r.scope2 || ""} disabled={r.source === "gei"} onChange={(_, d) => setRow(ix, { scope2: d.value })} />
                  </Table.Cell>
                  <Table.Cell>
                    <Input type="number" step="0.01" value={r.scope3Covered || ""} disabled={r.source === "gei"} onChange={(_, d) => setRow(ix, { scope3Covered: d.value })} />
                  </Table.Cell>
                  <Table.Cell className="num">{fmt(num(r.scope1) + num(r.scope2))}</Table.Cell>
                  <Table.Cell className="num">{expected !== null ? fmt(expected) : "—"}</Table.Cell>
                  <Table.Cell>{r.source === "gei" ? "módulo GEI" : "manual"}</Table.Cell>
                  <Table.Cell><Input value={r.note || ""} onChange={(_, d) => setRow(ix, { note: d.value })} /></Table.Cell>
                  <Table.Cell><Button icon="trash" basic size="tiny" onClick={() => removeRow(ix)} /></Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
        <Button basic onClick={addRow}>+ Agregar año</Button>

        {semaphore && (
          <div className={`sbti-note ${semaphore.status === "ok" ? "sbti-note--ok" : semaphore.status === "warn" ? "sbti-note--warn" : "sbti-note--bad"}`} style={{ marginTop: 12 }}>
            <b>A1+2 · {semaphore.year}:</b> real {fmt(semaphore.actual)} tCO₂e vs. trayectoria lineal {fmt(semaphore.expected)} tCO₂e ⇒ desvío {semaphore.deviationPercent > 0 ? "+" : ""}{fmt(semaphore.deviationPercent)}%.{" "}
            {semaphore.status === "ok" ? "En camino: por debajo de la trayectoria." : semaphore.status === "warn" ? "Atención: hasta +5% sobre la trayectoria." : "Desviado: más de +5% sobre la trayectoria — activar palancas de reducción y evaluar el plan."}
            <br />
            <span className="sbti-note__fine">
              Convención declarada del módulo: la banda del semáforo (0% / +5%) es una convención de gestión de
              Sustemia, no un umbral del SBTi.
            </span>
          </div>
        )}

        <div className="sbti-dash__grid2" style={{ marginTop: 12 }}>
          <div className="sbti-chartbox">
            <b className="sbti-chartbox__title">A1+2 — real vs. trayectoria</b>
            <TrajectoryChart baseYear={baseYear} targetYear={targetYear12} baseEmissions={r12.baseEmissions} targetEmissions={r12.targetEmissions} series={series12} />
          </div>
          <div className="sbti-chartbox">
            <b className="sbti-chartbox__title">A3 cubierto — real vs. trayectoria</b>
            {targetYear3 ? (
              <TrajectoryChart baseYear={baseYear} targetYear={targetYear3} baseEmissions={r3.baseEmissions} targetEmissions={r3.targetEmissions} series={series3} />
            ) : (
              <div className="sbti-hint">Definí el objetivo A3 en el Paso 4 para ver la trayectoria.</div>
            )}
          </div>
        </div>
      </div>

      <div className="sbti-dash__grid2">
        <div className="sbti-card">
          <h3><span className="sbti-tag">OBLIGACIÓN ANUAL</span> Divulgaciones públicas registradas</h3>
          <Table celled compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Año informado</Table.HeaderCell>
                <Table.HeaderCell>Medio</Table.HeaderCell>
                <Table.HeaderCell>Fecha</Table.HeaderCell>
                <Table.HeaderCell>Referencia</Table.HeaderCell>
                <Table.HeaderCell></Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {disclosures.map((d, ix) => (
                <Table.Row key={ix}>
                  <Table.Cell><Input type="number" value={d.reportedYear || ""} onChange={(_, e) => setDisclosure(ix, { reportedYear: e.value })} style={{ width: 84 }} /></Table.Cell>
                  <Table.Cell><Select compact options={CHANNEL_OPTIONS} value={d.channel || "annual_report"} onChange={(_, e) => setDisclosure(ix, { channel: e.value })} /></Table.Cell>
                  <Table.Cell><Input type="date" value={d.date || ""} onChange={(_, e) => setDisclosure(ix, { date: e.value })} /></Table.Cell>
                  <Table.Cell><Input value={d.reference || ""} onChange={(_, e) => setDisclosure(ix, { reference: e.value })} /></Table.Cell>
                  <Table.Cell><Button icon="trash" basic size="tiny" onClick={() => removeDisclosure(ix)} /></Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          <Button basic size="small" onClick={addDisclosure}>+ Registrar divulgación</Button>
          <p className="sbti-hint">
            No hay un lugar único obligatorio: vale informe anual, memoria de sostenibilidad, sitio web y/o CDP — lo
            exigido es que sea <b>público y anual</b>.
          </p>
        </div>

        <div className="sbti-card">
          <h3><span className="sbti-tag">A 5 AÑOS</span> Revisión quinquenal obligatoria</h3>
          {quinquenal ? (
            <>
              <div className="sbti-kpis">
                <div className="sbti-kpi sbti-kpi--alert"><div className="sbti-kpi__l">Disparo de la revisión</div><div className="sbti-kpi__v" style={{ fontSize: 18 }}>{fFecha(quinquenal.date)}</div></div>
                <div className="sbti-kpi"><div className="sbti-kpi__l">Días restantes</div><div className="sbti-kpi__v">{diasRestantes > 0 ? diasRestantes : "¡vencido!"}</div></div>
              </div>
              <table className="sbti-plain-table">
                <thead><tr><th>Obligación</th><th>Plazo</th></tr></thead>
                <tbody>
                  <tr><td>Revisar todos los objetivos activos y enviar resultados por el formulario online</td><td className="num">{fFecha(quinquenalResultados?.date)}</td></tr>
                  <tr><td>Si hace falta actualizar: presentar los objetivos ajustados a validación</td><td className="num">{fFecha(quinquenalRetargets?.date)}</td></tr>
                </tbody>
              </table>
            </>
          ) : (
            <p className="sbti-hint">
              Cargá la fecha de aprobación en el Paso 6 y acá se calculan el disparo (+{params.revision_anios ?? 5}{" "}
              años), la entrega de resultados y la re-presentación.
            </p>
          )}
          <h3 style={{ marginTop: 16 }}>Disparadores de recálculo (marcar si ocurren)</h3>
          {TRIGGERS.map(([id, label]) => (
            <Checkbox
              key={id}
              className="sbti-paso7__trigger"
              label={label}
              checked={criteria[id]?.status === "ok"}
              onChange={(_, d) => setTrigger(id, d.checked)}
            />
          ))}
        </div>
      </div>

      <div className="sbti-card">
        <h3><span className="sbti-tag">GUÍA §7</span> Palancas de reducción del sector</h3>
        <div className="sbti-dash__grid2">
          <div>
            <div className="sbti-paso7__pgram">Tiers 1–2 · Eficiencia + energía renovable en la cadena</div>
            <ul className="sbti-paso7__list">
              <li>Identificar, premiar e incentivar a los proveedores de mejor desempeño</li>
              <li>Desinvertir de proveedores de bajo desempeño</li>
              <li>Invertir en eficiencia energética y mejoras en proveedores medios y top</li>
              <li>Apalancar y escalar programas existentes (NRDC Clean by Design, IFC)</li>
              <li>Invertir en energía renovable en tiers 1 y 2</li>
            </ul>
            <p className="sbti-hint">En Sustemia: se monitorea con el módulo GEI, el checklist SeH/ambiental y el Plan Anual.</p>
          </div>
          <div>
            <div className="sbti-paso7__pgram">Tiers 3–4 · Transformar la paleta de materiales</div>
            <ul className="sbti-paso7__list">
              <li>Acelerar y escalar materiales ambientalmente preferibles (EPMs)</li>
              <li>Eficiencia y renovables en procesos (p. ej., polimerización)</li>
              <li>Intervenciones a nivel finca/rancho (agricultura regenerativa)</li>
              <li>I+D para materiales de «impacto cero»</li>
            </ul>
            <p className="sbti-hint">
              Palancas transversales: eficiencia de material, sustitución (poliéster virgen → rPoly), cambios de
              sourcing e inversiones puntuales en la cadena.
            </p>
          </div>
        </div>
      </div>

      <div className="sbti-card">
        <h3><span className="sbti-tag">GUÍA §8</span> Colaboraciones e iniciativas del sector</h3>
        <div className="sbti-chips">
          {COLABORACIONES.map((c) => <span className="sbti-chip sbti-chip--plain" key={c}>{c}</span>)}
        </div>
        <div className="sbti-src">
          <b>Fuente:</b> guía sectorial 2019, secciones 7 (Figura 9) y 8 (colaboraciones y recursos), y casos
          (Levi's/PaCT, Nike Energy &amp; Carbon Program). La guía aclara que ese marco es ilustrativo, no
          exhaustivo.
        </div>
      </div>
    </div>
  );
}
